import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import quoteLogo from '../assets/logo.png';
import { buildInvoicePDF, downloadInvoiceWord, loadImageAsset, imgSrc } from './_all_admin';
import { INVOICE_THEMES, loadThemeChoice, saveThemeChoice } from '../print/docThemes';
import MenuButton from '../components/MenuButton';

// ═══════════════════════════════════════════════════════════════
//  INVOICE PREVIEW — full page, live template switching
// ═══════════════════════════════════════════════════════════════
// Mirrors the quotation preview: the document on screen is the real PDF, and
// the next template renders into a hidden second iframe so switching never
// blanks the page.

export default function InvoicePreview() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [brand, setBrand] = useState(null);       // { settings, logoAsset }
  const [theme, setTheme] = useState(loadThemeChoice);
  const [visibleUrl, setVisibleUrl] = useState('');
  const [pendingUrl, setPendingUrl] = useState('');
  const [building, setBuilding] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const urlsRef = useRef(new Set());
  const visibleRef = useRef('');

  // The PDF needs the company settings and the logo bitmap, same as the
  // invoice screen loads for itself.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [inv, cfg] = await Promise.all([
          api.get(`/invoices/${id}`).then(r => r.data),
          api.get('/settings').then(r => r.data || {}).catch(() => ({})),
        ]);
        if (cancelled) return;
        setInvoice(inv);
        // The PDF draws the signature above the authorised-signatory line, so
        // load it alongside the logo or the preview would differ from the file
        // the invoice screen produces.
        const [logoAsset, signatureAsset] = await Promise.all([
          loadImageAsset(cfg.logo ? imgSrc(cfg.logo) : quoteLogo, { sampleBg: true }),
          cfg.signature ? loadImageAsset(imgSrc(cfg.signature)) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setBrand({ settings: cfg, logoAsset, signatureAsset });
      } catch {
        if (!cancelled) setError('Could not load this invoice.');
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!invoice || !brand) return;
    let cancelled = false;
    setBuilding(true);
    (async () => {
      try {
        const doc = await buildInvoicePDF(invoice, theme, brand);
        if (cancelled) return;
        const url = doc.output('bloburl').toString();
        urlsRef.current.add(url);
        setError('');
        if (visibleRef.current) {
          setPendingUrl(url);
        } else {
          visibleRef.current = url;
          setVisibleUrl(url);
          setBuilding(false);
        }
      } catch {
        if (!cancelled) { setError('Could not build the preview.'); setBuilding(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [invoice, brand, theme]);

  useEffect(() => {
    const urls = urlsRef.current;
    return () => { urls.forEach(u => URL.revokeObjectURL(u)); urls.clear(); };
  }, []);

  const promoteBuffer = () => {
    if (!pendingUrl) return;
    const previous = visibleRef.current;
    visibleRef.current = pendingUrl;
    setVisibleUrl(pendingUrl);
    setPendingUrl('');
    setBuilding(false);
    if (previous && previous !== pendingUrl) {
      URL.revokeObjectURL(previous);
      urlsRef.current.delete(previous);
    }
  };

  const pickTheme = (key) => { setTheme(key); saveThemeChoice(key); };

  const withDoc = async (fn, failMsg) => {
    if (!invoice || !brand) return;
    setBusy(true);
    try { await fn(await buildInvoicePDF(invoice, theme, brand)); }
    catch { toast.error(failMsg); }
    finally { setBusy(false); }
  };

  const fileName = () => `${invoice.invoiceNo || 'invoice'}.pdf`;

  const download = () => withDoc(doc => doc.save(fileName()), 'Could not generate PDF');

  const word = async () => {
    setBusy(true);
    try { await downloadInvoiceWord(invoice, theme, brand); }
    catch { toast.error('Could not generate Word file'); }
    finally { setBusy(false); }
  };

  const share = (channel) => withDoc(async (doc) => {
    const message = `Hi ${invoice.buyerName || 'there'}, here is invoice ${invoice.invoiceNo || ''} from Wedoo Interiors.`;
    try {
      const file = new File([doc.output('blob')], fileName(), { type: 'application/pdf' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Invoice ${invoice.invoiceNo || ''}`, text: message });
        return;
      }
    } catch (err) {
      if (err && err.name === 'AbortError') return;
    }
    doc.save(fileName());
    const note = ' The PDF has just been downloaded on this device — please attach it here.';
    if (channel === 'whatsapp') {
      const params = new URLSearchParams();
      const phone = (invoice.buyerMobile || '').replace(/\D/g, '');
      if (phone) params.set('phone', phone.length === 10 ? `91${phone}` : phone);
      params.set('text', message + note);
      window.open(`https://api.whatsapp.com/send?${params.toString()}`, '_blank');
    } else {
      window.location.href = `mailto:?subject=${encodeURIComponent(`Invoice ${invoice.invoiceNo || ''} - Wedoo Interiors`)}&body=${encodeURIComponent(message + note)}`;
    }
  }, 'Could not share');

  if (error && !invoice) return <p className="admin-empty">{error}</p>;
  if (!invoice) return <p className="admin-empty">Loading...</p>;

  return (
    <div className="qp">
      <div className="qp-head">
        <div>
          <button className="qp-back" onClick={() => navigate('/admin/invoice')}>← Back to Invoices</button>
          <p className="qp-eyebrow">Preview</p>
          <h1 className="qp-title">
            {invoice.invoiceNo || 'Invoice'}
            {invoice.buyerName && <span style={{ color: 'var(--muted)', fontSize: '1rem' }}> · {invoice.buyerName}</span>}
          </h1>
        </div>
        <div className="qp-actions">
          <Link to="/admin/invoice" className="btn btn-dark" style={{ fontSize: '0.7rem' }}>✎ Invoices</Link>
          <MenuButton
            icon="📤" label="Share" disabled={busy}
            items={[
              { icon: '📱', label: 'WhatsApp', onClick: () => share('whatsapp') },
              { icon: '✉️', label: 'Email', onClick: () => share('email') },
            ]} />
          <MenuButton
            icon="⬇" label="Download" disabled={busy} variant="btn-gold"
            items={[
              { icon: '📄', label: 'PDF', onClick: download },
              { icon: '📝', label: 'Word', onClick: word },
            ]} />
        </div>
      </div>

      <div className="qp-body">
        <aside className="qp-rail">
          <p className="qp-rail-title">Template</p>
          {INVOICE_THEMES.map(t => (
            <button
              key={t.key}
              onClick={() => pickTheme(t.key)}
              title={t.description}
              className={`qp-tpl${theme === t.key ? ' is-active' : ''}`}>
              <span className="qp-dot" style={{ background: t.swatch }} />
              <span className="qp-tpl-name">{t.label}</span>
            </button>
          ))}
        </aside>

        <div className="qp-stage">
          {building && <div className="qp-loading">Rendering {INVOICE_THEMES.find(t => t.key === theme)?.label || ''}…</div>}
          {error && !building && <div className="qp-loading">{error}</div>}
          {visibleUrl && (
            <iframe
              title={`Invoice ${invoice.invoiceNo || ''} preview`}
              src={visibleUrl}
              className="qp-frame"
            />
          )}
          {pendingUrl && (
            <iframe
              title="Loading next template"
              src={pendingUrl}
              className="qp-frame qp-frame-back"
              aria-hidden="true"
              onLoad={promoteBuffer}
            />
          )}
        </div>
      </div>
    </div>
  );
}
